import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Droplets, AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react';
import { useHydrationGroups } from '../../contexts/HydrationGroupsContext';
import { usePets } from '../../contexts/PetsContext';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { ROUTES, HYDRATION_INTERVALS, VALIDATION } from '../../config/constants';

/**
 * Edición de un balde de agua existente.
 *
 * Editable:
 *  - name
 *  - hydrationIntervalHours (chip selector)
 *
 * Listado read-only de las mascotas que comparten este balde — se editan
 * desde el form de cada mascota (toggle "Trackear agua" + selector).
 *
 * Eliminación: soft-delete con confirmación. Bloqueado si quedan pets activas
 * referenciando el grupo (error HYDRATION_GROUP_HAS_ACTIVE_PETS).
 */

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_HYDRATION_INTERVAL: 'hydrationIntervalHours',
};

const EditWaterGroupPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { getGroupById, updateHydrationGroup, softDeleteHydrationGroup, isLoading } =
    useHydrationGroups();
  const { pets } = usePets();
  const toast = useToast();

  const group = getGroupById(groupId);

  const [name, setName] = useState('');
  const [intervalHours, setIntervalHours] = useState(12);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!group) return;
    setName(group.name ?? '');
    setIntervalHours(group.hydrationIntervalHours ?? 12);
  }, [group]);

  // Pets activas que apuntan a este balde.
  const associatedPets = pets.filter(
    (p) => p.hydrationGroupId === groupId && !p.deletedAt
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          className="self-start"
        >
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          className="self-start"
        >
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">
            Este balde no existe o fue eliminado.
          </p>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    const updates = {};
    if (name.trim() !== group.name) {
      if (!name.trim()) {
        setErrors({ name: 'El nombre es requerido' });
        return;
      }
      updates.name = name;
    }
    if (intervalHours !== group.hydrationIntervalHours) {
      if (!HYDRATION_INTERVALS.includes(intervalHours)) {
        setErrors({ hydrationIntervalHours: 'Frecuencia inválida' });
        return;
      }
      updates.hydrationIntervalHours = intervalHours;
    }

    if (Object.keys(updates).length === 0) {
      toast.info('No hay cambios para guardar.');
      return;
    }

    try {
      setSubmitting(true);
      await updateHydrationGroup(groupId, updates);
      toast.success('Balde actualizado.');
      navigate(ROUTES.INVENTORY, { replace: true });
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo actualizar el balde.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await softDeleteHydrationGroup(groupId);
      toast.success('Balde eliminado.');
      navigate(ROUTES.INVENTORY, { replace: true });
    } catch (err) {
      if (err.code === 'HYDRATION_GROUP_HAS_ACTIVE_PETS') {
        toast.error(err.message);
      } else {
        toast.error(err.message || 'No se pudo eliminar el balde.');
      }
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="self-start"
      >
        Volver
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Editar balde</h1>
        <p className="text-sm text-slate-500">Cambiá el nombre o la frecuencia.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-lg bg-sky-100 p-2.5">
              <Droplets className="h-5 w-5 text-sky-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Datos del balde</h2>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Input
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              maxLength={VALIDATION.HYDRATION_GROUP_NAME_MAX}
            />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Frecuencia
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {HYDRATION_INTERVALS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setIntervalHours(h)}
                    className={[
                      'flex-1 min-w-[60px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                      intervalHours === h
                        ? 'border-sky-500 bg-sky-50 text-sky-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              {errors.hydrationIntervalHours && (
                <p className="mt-1.5 text-sm text-status-red">
                  {errors.hydrationIntervalHours}
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="font-semibold text-slate-900 mb-3">
            Mascotas con este balde
          </h2>
          {associatedPets.length === 0 ? (
            <p className="text-sm text-slate-500">
              Ninguna mascota toma agua de este balde todavía. Activá el tracking
              de agua al editar una mascota y elegí este balde.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {associatedPets.map((pet) => (
                <li
                  key={pet.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                >
                  <span className="text-sm text-slate-800">{pet.name}</span>
                  <Link
                    to={`/pets/${pet.id}/edit`}
                    className="text-xs text-brand-700 hover:underline"
                  >
                    Editar
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            disabled={submitting || deleting}
            leftIcon={Trash2}
          >
            Eliminar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={submitting}
          >
            Guardar cambios
          </Button>
        </div>
      </form>

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="¿Eliminar este balde?"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg bg-yellow-50 p-3">
            <AlertTriangle className="h-5 w-5 text-yellow-700 mt-0.5" />
            <p className="text-sm text-yellow-900">
              {associatedPets.length > 0
                ? `Este balde tiene ${associatedPets.length} mascota${associatedPets.length === 1 ? '' : 's'} activa${associatedPets.length === 1 ? '' : 's'}. Primero desactivá el tracking de agua en cada una.`
                : 'Esto va a marcar el balde como eliminado. Los registros históricos quedan en la bitácora.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={handleDelete}
              isLoading={deleting}
              disabled={associatedPets.length > 0}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditWaterGroupPage;
